import { supabase } from '../lib/supabase';

// Project Management
export const projectService = {
  // Get all projects for current user (owned or collaborated)
  async getUserProjects() {
    const { data, error } = await supabase
      ?.from('projects')
      ?.select(`
        *,
        owner:user_profiles!projects_owner_id_fkey(id, full_name, email),
        collaborators:project_collaborators(
          id,
          role,
          permissions,
          user:user_profiles!project_collaborators_user_id_fkey(id, full_name, email)
        ),
        video_files(id, name, file_size, duration, format),
        render_jobs(id, name, status, progress, created_at)
      `)
      ?.order('created_at', { ascending: false });

    return { data, error };
  },

  // Create new project - UPDATED to include owner_id
  async createProject(projectData) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) return { data: null, error: authError };

      const { data, error } = await supabase
        ?.from('projects')
        ?.insert({
          name: projectData?.name,
          description: projectData?.description,
          status: projectData?.status || 'draft',
          budget: projectData?.budget,
          start_date: projectData?.startDate,
          end_date: projectData?.endDate,
          owner_id: user?.id, // Ensure owner_id is set
          metadata: projectData?.metadata || {}
        })
        ?.select()
        ?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Update project
  async updateProject(projectId, updates) {
    const { data, error } = await supabase
      ?.from('projects')
      ?.update(updates)
      ?.eq('id', projectId)
      ?.select()
      ?.single();

    return { data, error };
  },

  // Delete project
  async deleteProject(projectId) {
    const { data, error } = await supabase
      ?.from('projects')
      ?.delete()
      ?.eq('id', projectId);

    return { data, error };
  },

  // Get project details
  async getProjectDetails(projectId) {
    const { data, error } = await supabase
      ?.from('projects')
      ?.select(`
        *,
        owner:user_profiles!projects_owner_id_fkey(id, full_name, email, avatar_url),
        collaborators:project_collaborators(
          id,
          role,
          permissions,
          user:user_profiles!project_collaborators_user_id_fkey(id, full_name, email, avatar_url)
        ),
        video_files(id, name, file_path, file_size, duration, resolution, format, created_at),
        credits(id, person_name, role, character_name, order_index),
        render_jobs(id, name, quality, status, progress, created_at, completed_at),
        activity_logs(id, action, details, created_at, user:user_profiles(full_name))
      `)
      ?.eq('id', projectId)
      ?.single();

    return { data, error };
  }
};

// Credit Management
export const creditService = {
  // Get credits for project
  async getProjectCredits(projectId) {
    const { data, error } = await supabase
      ?.from('credits')
      ?.select('*')
      ?.eq('project_id', projectId)
      ?.order('order_index', { ascending: true });

    return { data, error };
  },

  // Create credit
  async createCredit(creditData) {
    const { data, error } = await supabase
      ?.from('credits')
      ?.insert({
        project_id: creditData?.projectId,
        person_name: creditData?.personName,
        role: creditData?.role,
        character_name: creditData?.characterName,
        order_index: creditData?.orderIndex || 0
      })
      ?.select()
      ?.single();

    return { data, error };
  },

  // Update credit
  async updateCredit(creditId, updates) {
    const { data, error } = await supabase
      ?.from('credits')
      ?.update(updates)
      ?.eq('id', creditId)
      ?.select()
      ?.single();

    return { data, error };
  },

  // Delete credit
  async deleteCredit(creditId) {
    const { data, error } = await supabase
      ?.from('credits')
      ?.delete()
      ?.eq('id', creditId);

    return { data, error };
  },

  // Bulk create credits
  async createCreditsFromExcel(projectId, creditsData) {
    const creditsToInsert = creditsData?.map((credit, index) => ({
      project_id: projectId,
      person_name: credit?.personName,
      role: credit?.role,
      character_name: credit?.characterName,
      order_index: index
    }));

    const { data, error } = await supabase
      ?.from('credits')
      ?.insert(creditsToInsert)
      ?.select();

    return { data, error };
  }
};

// Credit Creation Service - NEW
export const creditCreationService = {
  // Get user's projects for credit creation
  async getProjectsForCredits() {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) return { data: [], error: authError };

      const { data, error } = await supabase
        ?.from('projects')
        ?.select(`
          id,
          name,
          description,
          status,
          created_at,
          updated_at,
          metadata,
          credits(id, person_name, role, character_name, order_index)
        `)
        ?.eq('owner_id', user?.id)
        ?.order('updated_at', { ascending: false });

      return { data: data || [], error };
    } catch (error) {
      return { data: [], error };
    }
  },

  // Create project with credits
  async createProjectWithCredits(projectData, creditsData = []) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) return { data: null, error: authError };

      // Create project
      const { data: project, error: projectError } = await supabase
        ?.from('projects')
        ?.insert({
          name: projectData?.name,
          description: projectData?.description || 'Film credits project',
          status: projectData?.status || 'draft',
          owner_id: user?.id,
          metadata: {
            previewStyle: projectData?.style || 'title_card',
            ...projectData?.metadata
          }
        })
        ?.select()
        ?.single();

      if (projectError) throw projectError;

      // Create credits if provided
      if (creditsData?.length > 0) {
        const creditsToInsert = creditsData?.map((credit, index) => ({
          project_id: project?.id,
          person_name: credit?.personName || credit?.name,
          role: credit?.role,
          character_name: credit?.characterName || credit?.character,
          order_index: credit?.orderIndex ?? index,
          created_by: user?.id
        }));

        const { error: creditsError } = await supabase
          ?.from('credits')
          ?.insert(creditsToInsert);

        if (creditsError) throw creditsError;
      }

      return { data: project, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Update credits order
  async updateCreditsOrder(projectId, creditsWithOrder) {
    try {
      for (const credit of creditsWithOrder) {
        const { error } = await supabase
          ?.from('credits')
          ?.update({ 
            order_index: credit?.order,
            person_name: credit?.name,
            character_name: credit?.character
          })
          ?.eq('id', credit?.id)
          ?.eq('project_id', projectId);

        if (error) throw error;
      }

      return { data: true, error: null };
    } catch (error) {
      return { data: false, error };
    }
  },

  // Bulk replace credits for project
  async bulkReplaceCredits(projectId, newCredits) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) return { data: null, error: authError };

      // Delete existing credits
      const { error: deleteError } = await supabase
        ?.from('credits')
        ?.delete()
        ?.eq('project_id', projectId);

      if (deleteError) throw deleteError;

      // Insert new credits
      if (newCredits?.length > 0) {
        const creditsToInsert = newCredits?.map((credit, index) => ({
          project_id: projectId,
          person_name: credit?.name,
          role: credit?.role,
          character_name: credit?.character,
          order_index: credit?.order ?? index,
          created_by: user?.id
        }));

        const { data, error: insertError } = await supabase
          ?.from('credits')
          ?.insert(creditsToInsert)
          ?.select();

        if (insertError) throw insertError;

        return { data, error: null };
      }

      return { data: [], error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get project with credits for editing
  async getProjectForEditing(projectId) {
    try {
      const { data, error } = await supabase
        ?.from('projects')
        ?.select(`
          *,
          credits(
            id,
            person_name,
            role,
            character_name,
            order_index,
            created_at
          ),
          owner:user_profiles!projects_owner_id_fkey(full_name, email)
        `)
        ?.eq('id', projectId)
        ?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Search and filter credits
  async searchCredits(projectId, searchTerm, roleFilter = null) {
    try {
      let query = supabase
        ?.from('credits')
        ?.select('*')
        ?.eq('project_id', projectId);

      if (searchTerm) {
        query = query?.or(`person_name.ilike.%${searchTerm}%,character_name.ilike.%${searchTerm}%`);
      }

      if (roleFilter) {
        query = query?.eq('role', roleFilter);
      }

      const { data, error } = await query?.order('order_index', { ascending: true });

      return { data: data || [], error };
    } catch (error) {
      return { data: [], error };
    }
  },

  // Get credits summary for project
  async getCreditsSummary(projectId) {
    try {
      const { data, error } = await supabase
        ?.from('credits')
        ?.select('role, person_name, character_name')
        ?.eq('project_id', projectId);

      if (error) throw error;

      const summary = {
        totalCredits: data?.length || 0,
        byRole: {},
        castMembers: data?.filter(c => c?.character_name)?.length || 0
      };

      data?.forEach(credit => {
        const role = credit?.role;
        summary.byRole[role] = (summary?.byRole?.[role] || 0) + 1;
      });

      return { data: summary, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Validate credits data
  validateCredits(credits) {
    const errors = [];
    
    credits?.forEach((credit, index) => {
      if (!credit?.name?.trim()) {
        errors?.push(`Credit ${index + 1}: Name is required`);
      }
      if (!credit?.role) {
        errors?.push(`Credit ${index + 1}: Role is required`);
      }
      // Updated valid roles to match database enum exactly
      const validRoles = ['director', 'producer', 'executive_producer', 'actor', 'cinematographer', 'editor', 'composer', 'writer', 'other'];
      if (credit?.role && !validRoles?.includes(credit?.role)) {
        errors?.push(`Credit ${index + 1}: Invalid role "${credit?.role}"`);
      }
    });

    return {
      isValid: errors?.length === 0,
      errors
    };
  }
};

// Video File Management
export const videoService = {
  // Upload video file
  async uploadVideo(file, projectId, progressCallback) {
    try {
      const fileName = `${Date.now()}-${file?.name}`;
      const filePath = `${projectId}/${fileName}`;

      const { data: uploadData, error: uploadError } = await supabase?.storage
        ?.from('video-files')
        ?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
          onUploadProgress: progressCallback
        });

      if (uploadError) throw uploadError;

      // Create video file record
      const { data: videoData, error: videoError } = await supabase
        ?.from('video_files')
        ?.insert({
          project_id: projectId,
          name: file?.name,
          file_path: uploadData?.path,
          file_size: file?.size,
          format: file?.type?.split('/')?.[1]
        })
        ?.select()
        ?.single();

      return { data: videoData, error: videoError };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get video files for project
  async getProjectVideos(projectId) {
    const { data, error } = await supabase
      ?.from('video_files')
      ?.select('*')
      ?.eq('project_id', projectId)
      ?.order('created_at', { ascending: false });

    return { data, error };
  },

  // Delete video file
  async deleteVideo(videoId, filePath) {
    try {
      // Delete from storage
      const { error: storageError } = await supabase?.storage
        ?.from('video-files')
        ?.remove([filePath]);

      if (storageError) throw storageError;

      // Delete from database
      const { data, error } = await supabase
        ?.from('video_files')
        ?.delete()
        ?.eq('id', videoId);

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }
};

// Render Job Management
export const renderService = {
  // Get render jobs for project
  async getProjectRenders(projectId) {
    const { data, error } = await supabase
      ?.from('render_jobs')
      ?.select(`
        *,
        video_file:video_files(id, name),
        started_by_user:user_profiles!render_jobs_started_by_fkey(full_name)
      `)
      ?.eq('project_id', projectId)
      ?.order('created_at', { ascending: false });

    return { data, error };
  },

  // Create render job
  async createRenderJob(renderData) {
    const { data, error } = await supabase
      ?.from('render_jobs')
      ?.insert({
        project_id: renderData?.projectId,
        video_file_id: renderData?.videoFileId,
        name: renderData?.name,
        quality: renderData?.quality || 'medium',
        settings: renderData?.settings || {}
      })
      ?.select()
      ?.single();

    return { data, error };
  },

  // Update render job status
  async updateRenderJob(renderId, updates) {
    const { data, error } = await supabase
      ?.from('render_jobs')
      ?.update(updates)
      ?.eq('id', renderId)
      ?.select()
      ?.single();

    return { data, error };
  },

  // Get render queue (pending/processing jobs)
  async getRenderQueue() {
    const { data, error } = await supabase
      ?.from('render_jobs')
      ?.select(`
        *,
        project:projects(name),
        video_file:video_files(name),
        started_by_user:user_profiles!render_jobs_started_by_fkey(full_name)
      `)
      ?.in('status', ['pending', 'processing'])
      ?.order('created_at', { ascending: true });

    return { data, error };
  }
};

// Video Rendering Service - NEW (Add to existing renderService)
const videoRenderingService = {
  // Get user's render jobs across all projects they own or collaborate on
  async getUserProjectRenders(userId = null) {
    try {
      // Get current user if not provided
      if (!userId) {
        const { data: { user }, error: authError } = await supabase?.auth?.getUser();
        if (authError || !user) return { data: [], error: authError };
        userId = user?.id;
      }

      // Get projects user owns or collaborates on
      const { data: userProjects, error: projectsError } = await supabase
        ?.from('projects')
        ?.select(`
          id,
          name,
          owner_id,
          render_jobs(
            id,
            name,
            status,
            quality,
            progress,
            settings,
            error_message,
            created_at,
            started_at,
            completed_at,
            output_path,
            project_id,
            video_file:video_files(id, name, format, resolution, file_size, duration),
            started_by_user:user_profiles!render_jobs_started_by_fkey(full_name)
          )
        `)
        ?.or(`owner_id.eq.${userId},project_collaborators.user_id.eq.${userId}`)
        ?.order('updated_at', { ascending: false });

      if (projectsError) throw projectsError;

      return { data: userProjects || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  // Get render queue with project context for user
  async getUserRenderQueue() {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) return { data: [], error: authError };

      const { data, error } = await supabase
        ?.from('render_jobs')
        ?.select(`
          *,
          project:projects!inner(
            id,
            name,
            owner_id,
            project_collaborators(user_id)
          ),
          video_file:video_files(id, name, format, resolution, file_size),
          started_by_user:user_profiles!render_jobs_started_by_fkey(full_name)
        `)
        ?.in('status', ['pending', 'processing'])
        ?.or(`project.owner_id.eq.${user?.id},project.project_collaborators.user_id.eq.${user?.id}`)
        ?.order('created_at', { ascending: true });

      return { data: data || [], error };
    } catch (error) {
      return { data: [], error };
    }
  },

  // Get completed renders for user with download capability
  async getUserCompletedRenders() {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) return { data: [], error: authError };

      const { data, error } = await supabase
        ?.from('render_jobs')
        ?.select(`
          *,
          project:projects!inner(
            id,
            name,
            owner_id,
            project_collaborators(user_id)
          ),
          video_file:video_files(id, name, format, resolution, file_size, duration),
          started_by_user:user_profiles!render_jobs_started_by_fkey(full_name)
        `)
        ?.eq('status', 'completed')
        ?.or(`project.owner_id.eq.${user?.id},project.project_collaborators.user_id.eq.${user?.id}`)
        ?.order('completed_at', { ascending: false });

      return { data: data || [], error };
    } catch (error) {
      return { data: [], error };
    }
  },

  // Download render file with signed URL
  async getDownloadUrl(renderJob) {
    try {
      if (!renderJob?.output_path) {
        return { data: null, error: { message: 'No output file available' } };
      }

      // Get signed URL for download
      const { data, error } = await supabase?.storage
        ?.from('video-files')
        ?.createSignedUrl(renderJob?.output_path, 3600); // 1 hour expiry

      if (error) throw error;

      return { data: data?.signedUrl, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get render statistics for user
  async getUserRenderStats() {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) return { data: null, error: authError };

      // Get all user's render jobs
      const { data: allRenders, error: rendersError } = await supabase
        ?.from('render_jobs')
        ?.select(`
          id,
          status,
          quality,
          created_at,
          completed_at,
          project:projects!inner(owner_id, project_collaborators(user_id))
        `)
        ?.or(`project.owner_id.eq.${user?.id},project.project_collaborators.user_id.eq.${user?.id}`);

      if (rendersError) throw rendersError;

      const stats = {
        total: allRenders?.length || 0,
        completed: allRenders?.filter(r => r?.status === 'completed')?.length || 0,
        processing: allRenders?.filter(r => r?.status === 'processing')?.length || 0,
        pending: allRenders?.filter(r => r?.status === 'pending')?.length || 0,
        failed: allRenders?.filter(r => r?.status === 'failed')?.length || 0,
        byQuality: {
          ultra: allRenders?.filter(r => r?.quality === 'ultra')?.length || 0,
          high: allRenders?.filter(r => r?.quality === 'high')?.length || 0,
          medium: allRenders?.filter(r => r?.quality === 'medium')?.length || 0,
          low: allRenders?.filter(r => r?.quality === 'low')?.length || 0
        }
      };

      return { data: stats, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};

// Extend existing renderService with new video rendering functions
export const extendedRenderService = {
  ...renderService, // Keep existing render service functions
  ...videoRenderingService // Add new video rendering functions
};

// User Management (for admins)
export const userService = {
  // Get all users (admin only)
  async getAllUsers() {
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.select('*')
      ?.order('created_at', { ascending: false });

    return { data, error };
  },

  // Update user profile
  async updateUserProfile(userId, updates) {
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.update(updates)
      ?.eq('id', userId)
      ?.select()
      ?.single();

    return { data, error };
  },

  // Get user stats
  async getUserStats() {
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.select('role')
      ?.order('created_at', { ascending: false });

    return { data, error };
  }
};

// Activity Logging
export const activityService = {
  // Log activity
  async logActivity(projectId, action, details = {}) {
    const { data, error } = await supabase
      ?.from('activity_logs')
      ?.insert({
        project_id: projectId,
        action,
        details
      })
      ?.select()
      ?.single();

    return { data, error };
  },

  // Get project activity
  async getProjectActivity(projectId, limit = 50) {
    const { data, error } = await supabase
      ?.from('activity_logs')
      ?.select(`
        *,
        user:user_profiles(full_name, avatar_url)
      `)
      ?.eq('project_id', projectId)
      ?.order('created_at', { ascending: false })
      ?.limit(limit);

    return { data, error };
  }
};

// Real-time subscriptions
export const subscriptionService = {
  // Subscribe to project changes
  subscribeToProject(projectId, callback) {
    return supabase
      ?.channel(`project-${projectId}`)
      ?.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'projects', filter: `id=eq.${projectId}` },
        callback
      )
      ?.subscribe();
  },

  // Subscribe to render job updates
  subscribeToRenderJobs(callback) {
    return supabase
      ?.channel('render-jobs')
      ?.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'render_jobs' },
        callback
      )
      ?.subscribe();
  },

  // Unsubscribe from channel
  unsubscribe(subscription) {
    return supabase?.removeChannel(subscription);
  }
};

// Admin Dashboard Service - NEW
export const adminService = {
  // Get admin dashboard stats
  async getDashboardStats() {
    try {
      // Get total users count
      const { count: totalUsers, error: usersError } = await supabase
        ?.from('user_profiles')
        ?.select('id', { count: 'exact' });

      if (usersError) throw usersError;

      // Get total projects count
      const { count: totalProjects, error: projectsError } = await supabase
        ?.from('projects')
        ?.select('id', { count: 'exact' });

      if (projectsError) throw projectsError;

      // Get total render jobs count
      const { count: totalRenders, error: rendersError } = await supabase
        ?.from('render_jobs')
        ?.select('id', { count: 'exact' });

      if (rendersError) throw rendersError;

      // Get active render jobs count
      const { count: activeRenders, error: activeRendersError } = await supabase
        ?.from('render_jobs')
        ?.select('id', { count: 'exact' })
        ?.in('status', ['pending', 'processing']);

      if (activeRendersError) throw activeRendersError;

      // Get completed renders today
      const today = new Date()?.toISOString()?.split('T')?.[0];
      const { count: rendersToday, error: rendersTodayError } = await supabase
        ?.from('render_jobs')
        ?.select('id', { count: 'exact' })
        ?.eq('status', 'completed')
        ?.gte('completed_at', `${today}T00:00:00.000Z`)
        ?.lte('completed_at', `${today}T23:59:59.999Z`);

      if (rendersTodayError) throw rendersTodayError;

      return {
        data: {
          totalUsers: totalUsers || 0,
          totalProjects: totalProjects || 0,
          totalRenders: totalRenders || 0,
          activeRenders: activeRenders || 0,
          rendersToday: rendersToday || 0
        },
        error: null
      };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get recent activities for admin dashboard
  async getRecentActivities(limit = 10) {
    const { data, error } = await supabase
      ?.from('activity_logs')
      ?.select(`
        id,
        action,
        details,
        created_at,
        user:user_profiles(full_name, email),
        project:projects(name)
      `)
      ?.order('created_at', { ascending: false })
      ?.limit(limit);

    return { data, error };
  },

  // Get system status data
  async getSystemStatus() {
    try {
      // Get render queue stats
      const { data: queueData, error: queueError } = await supabase
        ?.from('render_jobs')
        ?.select('status')
        ?.in('status', ['pending', 'processing']);

      if (queueError) throw queueError;

      const pendingJobs = queueData?.filter(job => job?.status === 'pending')?.length || 0;
      const processingJobs = queueData?.filter(job => job?.status === 'processing')?.length || 0;

      // Get storage usage from video files
      const { data: videoFiles, error: storageError } = await supabase
        ?.from('video_files')
        ?.select('file_size');

      if (storageError) throw storageError;

      const totalStorageBytes = videoFiles?.reduce((sum, file) => sum + (file?.file_size || 0), 0) || 0;
      const totalStorageGB = Math.round((totalStorageBytes / (1024 * 1024 * 1024)) * 100) / 100;

      // Get active sessions (users created in last 24 hours as proxy)
      const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000)?.toISOString();
      const { count: activeSessions, error: sessionsError } = await supabase
        ?.from('user_profiles')
        ?.select('id', { count: 'exact' })
        ?.gte('updated_at', yesterday);

      if (sessionsError) throw sessionsError;

      return {
        data: {
          renderQueue: {
            pending: pendingJobs,
            processing: processingJobs,
            total: pendingJobs + processingJobs
          },
          storage: {
            usedGB: totalStorageGB,
            totalGB: 100, // Mock total capacity
            percentage: Math.min((totalStorageGB / 100) * 100, 100)
          },
          activeSessions: activeSessions || 0
        },
        error: null
      };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get user growth data for charts
  async getUserGrowthData() {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('created_at')
        ?.order('created_at', { ascending: true });

      if (error) throw error;

      // Group by month for the last 6 months
      const sixMonthsAgo = new Date();
      sixMonthsAgo?.setMonth(sixMonthsAgo?.getMonth() - 6);

      const monthlyData = {};
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

      // Initialize last 6 months with 0
      for (let i = 5; i >= 0; i--) {
        const date = new Date();
        date?.setMonth(date?.getMonth() - i);
        const monthKey = months?.[date?.getMonth()];
        monthlyData[monthKey] = 0;
      }

      // Count users by month
      data?.forEach(user => {
        const userDate = new Date(user?.created_at);
        if (userDate >= sixMonthsAgo) {
          const monthKey = months?.[userDate?.getMonth()];
          if (monthlyData?.[monthKey] !== undefined) {
            monthlyData[monthKey]++;
          }
        }
      });

      // Convert to chart format with cumulative totals
      let cumulative = 0;
      const chartData = Object.entries(monthlyData)?.map(([name, value]) => {
        cumulative += value;
        return { name, value: cumulative };
      });

      return { data: chartData, error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  // Get render stats for charts
  async getRenderStatsData() {
    try {
      const { data, error } = await supabase
        ?.from('render_jobs')
        ?.select('status, created_at')
        ?.gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString());

      if (error) throw error;

      // Group by day of week
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const dailyStats = {};
      
      // Initialize with 0
      days?.forEach(day => {
        dailyStats[day] = 0;
      });

      // Count completed renders by day
      data?.forEach(job => {
        if (job?.status === 'completed') {
          const dayIndex = new Date(job?.created_at)?.getDay();
          const dayName = days?.[dayIndex];
          dailyStats[dayName]++;
        }
      });

      const chartData = Object.entries(dailyStats)?.map(([name, value]) => ({ name, value }));

      return { data: chartData, error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  // Get user role distribution
  async getUserRoleDistribution() {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('role');

      if (error) throw error;

      const roleCounts = {};
      data?.forEach(user => {
        const role = user?.role || 'user';
        roleCounts[role] = (roleCounts?.[role] || 0) + 1;
      });

      const chartData = Object.entries(roleCounts)?.map(([name, value]) => ({
        name: name?.charAt(0)?.toUpperCase() + name?.slice(1),
        value
      }));

      return { data: chartData, error: null };
    } catch (error) {
      return { data: [], error };
    }
  }
};

// User Dashboard Service - NEW
export const userDashboardService = {
  // Get user dashboard data (projects, stats, activities)
  async getUserDashboardData() {
    try {
      // Get user's projects with related data
      const { data: projects, error: projectsError } = await supabase
        ?.from('projects')?.select(`*,credits(id),render_jobs(id, status, progress, created_at),video_files(id, name, file_size)`)?.order('created_at', { ascending: false });

      if (projectsError) throw projectsError;

      // Get user's recent activities
      const { data: activities, error: activitiesError } = await supabase
        ?.from('activity_logs')
        ?.select(`
          id,
          action,
          details,
          created_at,
          project:projects(name)
        `)
        ?.order('created_at', { ascending: false })
        ?.limit(10);

      if (activitiesError) throw activitiesError;

      // Calculate user stats
      const totalProjects = projects?.length || 0;
      const completedProjects = projects?.filter(p => p?.status === 'completed')?.length || 0;
      const renderingQueue = projects?.reduce((sum, p) => {
        return sum + (p?.render_jobs?.filter(r => r?.status === 'processing')?.length || 0);
      }, 0);

      // Mock credits remaining - would need a subscription/billing table for real data
      const creditsRemaining = 150;

      const stats = {
        totalProjects,
        completedProjects,
        creditsRemaining,
        renderingQueue
      };

      return {
        data: {
          projects: projects || [],
          activities: activities || [],
          stats
        },
        error: null
      };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get user's projects with detailed info for dashboard
  async getUserProjects() {
    const { data, error } = await supabase
      ?.from('projects')
      ?.select(`
        *,
        credits(id),
        render_jobs(id, status, progress),
        video_files(id, name, file_size)
      `)
      ?.order('updated_at', { ascending: false });

    return { data, error };
  },

  // Get user's recent activities for dashboard
  async getUserActivities(limit = 10) {
    const { data, error } = await supabase
      ?.from('activity_logs')
      ?.select(`
        id,
        action,
        details,
        created_at,
        project:projects(name)
      `)
      ?.order('created_at', { ascending: false })
      ?.limit(limit);

    return { data, error };
  },

  // Get user dashboard stats
  async getUserStats() {
    try {
      // Get user's projects
      const { data: projects, error: projectsError } = await supabase
        ?.from('projects')
        ?.select(`
          id,
          status,
          render_jobs(status)
        `);

      if (projectsError) throw projectsError;

      const totalProjects = projects?.length || 0;
      const completedProjects = projects?.filter(p => p?.status === 'completed')?.length || 0;
      
      // Count rendering jobs
      const renderingQueue = projects?.reduce((sum, project) => {
        const processingJobs = project?.render_jobs?.filter(job => 
          job?.status === 'processing' || job?.status === 'pending'
        )?.length || 0;
        return sum + processingJobs;
      }, 0);

      // Mock credits - would come from user subscription/billing table
      const creditsRemaining = 150;

      return {
        data: {
          totalProjects,
          completedProjects,
          creditsRemaining,
          renderingQueue
        },
        error: null
      };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Format activity message for UI display
  formatActivityMessage(activity) {
    const { action, details, project } = activity;
    const projectName = project?.name || 'Unknown Project';

    const messages = {
      'project_created': `New project '${projectName}' created`,
      'project_updated': `Project '${projectName}' updated`,
      'project_deleted': `Project '${projectName}' deleted`,
      'video_uploaded': `Video uploaded to '${projectName}'`,
      'video_deleted': `Video deleted from '${projectName}'`,
      'render_started': `Render started for '${projectName}'`,
      'render_completed': `Video rendering completed for '${projectName}'`,
      'render_failed': `Render failed for '${projectName}'`,
      'credits_uploaded': `Excel credits uploaded to '${projectName}'`,
      'credits_updated': `Credits updated for '${projectName}'`,
      'collaboration_added': `Collaborator added to '${projectName}'`,
      'collaboration_removed': `Collaborator removed from '${projectName}'`
    };

    return messages?.[action] || `Activity in '${projectName}'`;
  }
};

// Account Settings Service - NEW
export const accountService = {
  // Get current user profile
  async getCurrentUserProfile() {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError) return { data: null, error: authError };
    if (!user) return { data: null, error: { message: 'User not authenticated' } };

    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.select('*')
      ?.eq('id', user?.id)
      ?.single();

    return { data, error };
  },

  // Update user profile
  async updateUserProfile(updates) {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError) return { data: null, error: authError };
    if (!user) return { data: null, error: { message: 'User not authenticated' } };

    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.update(updates)
      ?.eq('id', user?.id)
      ?.select()
      ?.single();

    return { data, error };
  },

  // Update user email (requires auth)
  async updateUserEmail(newEmail) {
    const { data, error } = await supabase?.auth?.updateUser({
      email: newEmail
    });

    if (!error && data?.user) {
      // Update profile table as well
      await supabase
        ?.from('user_profiles')
        ?.update({ email: newEmail })
        ?.eq('id', data?.user?.id);
    }

    return { data, error };
  },

  // Update user password
  async updateUserPassword(newPassword) {
    const { data, error } = await supabase?.auth?.updateUser({
      password: newPassword
    });

    return { data, error };
  },

  // Upload user avatar
  async uploadAvatar(file) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError) throw authError;
      if (!user) throw new Error('User not authenticated');

      const fileName = `${user?.id}-${Date.now()}.${file?.name?.split('.')?.pop()}`;
      const filePath = `avatars/${fileName}`;

      // Upload to storage
      const { data: uploadData, error: uploadError } = await supabase?.storage
        ?.from('user-avatars')
        ?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase?.storage
        ?.from('user-avatars')
        ?.getPublicUrl(uploadData?.path);

      // Update user profile with new avatar URL
      const { data: profileData, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.update({ avatar_url: urlData?.publicUrl })
        ?.eq('id', user?.id)
        ?.select()
        ?.single();

      if (profileError) throw profileError;

      return { data: profileData, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get user's subscription status (mock for now)
  async getUserSubscription() {
    const { data: profile, error } = await this.getCurrentUserProfile();
    if (error) return { data: null, error };

    // Mock subscription data based on user role
    const subscriptionMap = {
      'admin': {
        plan: 'Enterprise',
        status: 'active',
        renewalDate: '2025-01-15',
        features: ['Unlimited projects', 'Priority rendering', 'Advanced analytics', 'API access'],
        price: '$99/month'
      },
      'manager': {
        plan: 'Professional',
        status: 'active',
        renewalDate: '2025-01-15',
        features: ['Up to 50 projects', 'HD rendering', 'Team collaboration', 'Export history'],
        price: '$29/month'
      },
      'filmmaker': {
        plan: 'Basic',
        status: 'active',
        renewalDate: '2025-01-15',
        features: ['Up to 10 projects', 'Standard rendering', 'Basic exports'],
        price: '$9/month'
      },
      'user': {
        plan: 'Free',
        status: 'active',
        renewalDate: null,
        features: ['Up to 3 projects', 'Watermarked exports', 'Community support'],
        price: 'Free'
      }
    };

    const subscription = subscriptionMap?.[profile?.role] || subscriptionMap?.['user'];

    return { 
      data: {
        ...subscription,
        billingEmail: profile?.email,
        nextBillingDate: subscription?.renewalDate,
        creditsRemaining: profile?.role === 'admin' ? 'Unlimited' : 150
      }, 
      error: null 
    };
  },

  // Get user's notification preferences (mock)
  async getNotificationPreferences() {
    const { data: profile, error } = await this.getCurrentUserProfile();
    if (error) return { data: null, error };

    // Mock notification preferences
    const preferences = {
      email: {
        projectUpdates: true,
        renderComplete: true,
        systemAlerts: profile?.role === 'admin',
        weeklyDigest: true,
        marketingEmails: false
      },
      push: {
        renderComplete: true,
        projectShared: true,
        systemAlerts: profile?.role === 'admin',
        renderFailed: true
      },
      sms: {
        criticalAlerts: profile?.role === 'admin',
        renderComplete: false
      }
    };

    return { data: preferences, error: null };
  },

  // Update notification preferences
  async updateNotificationPreferences(preferences) {
    // In a real app, this would update a notifications table
    // For now, we'll store in user metadata
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError) return { data: null, error: authError };

    const { data, error } = await supabase?.auth?.updateUser({
      data: { 
        notification_preferences: preferences 
      }
    });

    return { data: preferences, error };
  },

  // Get user preferences for exports/rendering
  async getUserPreferences() {
    const { data: profile, error } = await this.getCurrentUserProfile();
    if (error) return { data: null, error };

    // Mock user preferences stored in metadata
    const defaultPreferences = {
      defaultExportFormat: 'mp4',
      defaultResolution: '1920x1080',
      defaultFrameRate: 24,
      defaultQuality: 'high',
      autoSaveInterval: 5, // minutes
      theme: 'system',
      language: 'en',
      timezone: 'UTC'
    };

    const preferences = profile?.metadata?.preferences || defaultPreferences;

    return { data: preferences, error: null };
  },

  // Update user preferences
  async updateUserPreferences(preferences) {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError) return { data: null, error: authError };

    const { data: currentProfile } = await this.getCurrentUserProfile();
    const updatedMetadata = {
      ...currentProfile?.metadata,
      preferences
    };

    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.update({ metadata: updatedMetadata })
      ?.eq('id', user?.id)
      ?.select()
      ?.single();

    return { data: preferences, error };
  },

  // Get payment methods (mock)
  async getPaymentMethods() {
    // Mock payment methods
    const paymentMethods = [
      {
        id: 'pm_1',
        type: 'credit_card',
        brand: 'visa',
        last4: '4242',
        expiryMonth: 12,
        expiryYear: 2025,
        isDefault: true,
        billingAddress: {
          line1: '123 Main St',
          city: 'San Francisco',
          state: 'CA',
          postalCode: '94105',
          country: 'US'
        }
      }
    ];

    return { data: paymentMethods, error: null };
  },

  // Get billing history (mock)
  async getBillingHistory() {
    const billingHistory = [
      {
        id: 'inv_1',
        date: '2024-10-01',
        amount: 2900, // in cents
        status: 'paid',
        description: 'Professional Plan - October 2024',
        downloadUrl: '#'
      },
      {
        id: 'inv_2',
        date: '2024-09-01',
        amount: 2900,
        status: 'paid',
        description: 'Professional Plan - September 2024',
        downloadUrl: '#'
      }
    ];

    return { data: billingHistory, error: null };
  },

  // Delete user account
  async deleteUserAccount() {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError) return { data: null, error: authError };

    // First, deactivate the user profile
    const { error: deactivateError } = await supabase
      ?.from('user_profiles')
      ?.update({ is_active: false })
      ?.eq('id', user?.id);

    if (deactivateError) return { data: null, error: deactivateError };

    // In a production app, you'd handle data cleanup, export, etc.
    // For now, just sign out the user
    const { error: signOutError } = await supabase?.auth?.signOut();

    return { data: { success: true }, error: signOutError };
  }
};